import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Download, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export function DataExportTab({ hotelId, hotelName }) {
  const [exportFormat, setExportFormat] = useState('json');
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState(null);

  const handleExport = async () => {
    setLoading(true);
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/hotels/${hotelId}/export?format=${exportFormat}`);
      const data = await response.json();
      
      if (data.success) {
        setExportData(data.data);
        
        const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${hotelId}_export_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`Data exported successfully! (${exportFormat.toUpperCase()})`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="owner-data-export-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" />
            Export Your Data
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Download all your hotel data for backup, analysis, or portability
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="bg-primary/5 border-primary/20">
            <AlertDescription>
              <strong>What's included:</strong> All bookings, guest data, rooms, blocked dates, and analytics
            </AlertDescription>
          </Alert>

          <div>
            <Label htmlFor="export-format">Export Format</Label>
            <Select value={exportFormat} onValueChange={setExportFormat}>
              <SelectTrigger id="export-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="json">JSON (Complete data with structure)</SelectItem>
                <SelectItem value="csv">CSV (Spreadsheet-friendly format)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            onClick={handleExport}
            disabled={loading}
            className="w-full md:w-auto"
            size="lg"
            data-testid="data-export-button"
          >
            <Download className="w-4 h-4 mr-2" />
            {loading ? 'Exporting...' : `Export as ${exportFormat.toUpperCase()}`}
          </Button>
        </CardContent>
      </Card>

      {exportData && (
        <Card>
          <CardHeader>
            <CardTitle>Export Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-primary/5 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Total Bookings</p>
                <p className="text-2xl font-bold">{exportData.analytics?.total_bookings || 0}</p>
              </div>
              <div className="bg-primary/5 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Total Guests</p>
                <p className="text-2xl font-bold">{exportData.analytics?.total_guests || 0}</p>
              </div>
              <div className="bg-primary/5 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">${exportData.analytics?.total_revenue || 0}</p>
              </div>
              <div className="bg-primary/5 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Total Nights</p>
                <p className="text-2xl font-bold">{exportData.analytics?.total_nights_booked || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Why Export Your Data?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex gap-2">
            <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
            <div>
              <p className="font-medium">Backup & Security</p>
              <p className="text-muted-foreground">Keep a local copy of all your important business data</p>
            </div>
          </div>
          <div className="flex gap-2">
            <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
            <div>
              <p className="font-medium">Business Analysis</p>
              <p className="text-muted-foreground">Import into Excel or your accounting software</p>
            </div>
          </div>
          <div className="flex gap-2">
            <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
            <div>
              <p className="font-medium">Data Portability</p>
              <p className="text-muted-foreground">Your data belongs to you - take it wherever you need</p>
            </div>
          </div>
          <div className="flex gap-2">
            <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
            <div>
              <p className="font-medium">Compliance</p>
              <p className="text-muted-foreground">Meet GDPR and data protection requirements</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
